import React, { useEffect, useMemo, useState } from 'react';
import { fetchUsabilityReport, type UsabilityReport } from '../../services/usabilityApi';
import XTAMSpinner from '../XTAMSpinner';
import './DashboardUsabilityCharts.css';

type Props = {
  token: string | null;
};

function formatRange(from: string, to: string): string {
  try {
    const f = new Date(from).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
    const t = new Date(to).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${f} – ${t}`;
  } catch {
    return `${from} – ${to}`;
  }
}

function MiniBars({
  items,
}: {
  items: Array<{ label: string; value: number; color: string }>;
}) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="dash-usab-bars" role="img" aria-label="Gráfica de barras">
      {items.map((item) => (
        <div key={item.label} className="dash-usab-bar-row">
          <span className="dash-usab-bar-label">{item.label}</span>
          <div className="dash-usab-bar-track">
            <div
              className="dash-usab-bar-fill"
              style={{ width: `${(item.value / max) * 100}%`, backgroundColor: item.color }}
            />
          </div>
          <span className="dash-usab-bar-value">{item.value.toLocaleString('es-CO')}</span>
        </div>
      ))}
    </div>
  );
}

const DashboardUsabilityCharts: React.FC<Props> = ({ token }) => {
  const [report, setReport] = useState<UsabilityReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchUsabilityReport(token);
        if (!cancelled) setReport(data);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(
            (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
              (e as Error)?.message ||
              'No se pudo cargar usabilidad.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const topUsers = useMemo(() => {
    if (!report?.clients?.length) return [];
    return [...report.clients]
      .sort((a, b) => b.activity.downloads - a.activity.downloads)
      .slice(0, 8)
      .map((c) => ({
        label: (c.username || c.email).split('@')[0],
        value: c.activity.downloads,
        color: '#a61b34',
      }));
  }, [report]);

  if (!token) return null;

  return (
    <section className="dash-usab" aria-labelledby="dash-usab-title">
      <header className="dash-usab-header">
        <div>
          <h2 id="dash-usab-title" className="dash-usab-title">
            2 · USABILIDAD (últimos 30 días)
          </h2>
          {report && (
            <p className="dash-usab-subtitle">{formatRange(report.range.from, report.range.to)}</p>
          )}
        </div>
      </header>

      {loading && <XTAMSpinner variant="inline" message="Cargando gráficas de usabilidad..." />}

      {!loading && error && <p className="dash-usab-error">{error}</p>}

      {!loading && report && (
        <div className="dash-usab-grid">
          <article className="dash-usab-card">
            <h3 className="dash-usab-card-title">Actividad global</h3>
            <MiniBars
              items={[
                { label: 'Grabaciones', value: report.totals.recordings, color: '#3b82f6' },
                { label: 'Descargas', value: report.totals.downloads, color: '#a61b34' },
                {
                  label: 'Streaming',
                  value: report.totals.streaming ?? 0,
                  color: '#22c55e',
                },
                { label: 'Perifoneo', value: report.totals.perifoneo, color: '#f59e0b' },
              ]}
            />
            <p className="dash-usab-footnote">{report.totals.users} usuarios con actividad</p>
          </article>

          <article className="dash-usab-card">
            <h3 className="dash-usab-card-title">Top descargas por usuario</h3>
            {topUsers.length === 0 ? (
              <p className="dash-usab-empty">Sin datos en el rango.</p>
            ) : (
              <MiniBars items={topUsers} />
            )}
          </article>
        </div>
      )}
    </section>
  );
};

export default DashboardUsabilityCharts;
